import { assertNever } from '@/lib/typescript/assertNever'
import { buttonVariants, type ButtonVariant } from '@/ui/buttons/button-styles/ButtonStyles'

type ButtonSurface = StrictExclude<ButtonVariant, 'link' | 'link-highlighted'>

export type LinkButtonVariant = 'text-link' | 'text-highlighted' | `button-${ButtonSurface}`

function isButtonSurfaceLinkVariant(
  variant: LinkButtonVariant,
): variant is `button-${ButtonSurface}` {
  return variant.startsWith('button-')
}

/** `<Link>` variant → `ButtonStyles` variant (CSS classes). */
export function toButtonStyleVariant(variant: LinkButtonVariant): ButtonVariant {
  if (variant === 'text-link') return 'link'
  if (variant === 'text-highlighted') return 'link-highlighted'

  if (!isButtonSurfaceLinkVariant(variant)) {
    return assertNever(variant)
  }

  switch (variant) {
    case 'button-primary':
      return 'primary'
    case 'button-secondary':
      return 'secondary'
    case 'button-tertiary':
      return 'tertiary'
    case 'button-quaternary':
      return 'quaternary'
    case 'button-positive':
      return 'positive'
    case 'button-negative':
      return 'negative'
    default:
      return assertNever(variant)
  }
}

// Same default as the rich-text parser (src/feature/cms/helpers/parser.tsx)
const FALLBACK_LINK_VARIANT: LinkButtonVariant = 'text-link'

export function isButtonVariant(value: unknown): value is ButtonVariant {
  return typeof value === 'string' && buttonVariants.includes(value as ButtonVariant)
}

// `ButtonStyles` variant to `<Link>` variant (CMS / BO data using legacy names)
// The guard is required: `linkSchema.variant` is a `z.custom` that validates nothing, so CMS content
// without a variant used to build `button-undefined` and throw in `assertNever`
export function fromButtonStyleVariant(variant: ButtonVariant | undefined): LinkButtonVariant {
  if (!isButtonVariant(variant)) return FALLBACK_LINK_VARIANT

  if (variant === 'link') return 'text-link'
  if (variant === 'link-highlighted') return 'text-highlighted'

  return `button-${variant}`
}

export function isTextLinkTone(variant: LinkButtonVariant): boolean {
  return variant.startsWith('text-')
}

export const linkButtonVariants: readonly LinkButtonVariant[] =
  buttonVariants.map(fromButtonStyleVariant)

export const linkButtonSurfaceVariants: readonly LinkButtonVariant[] = linkButtonVariants.filter(
  (variant) => !isTextLinkTone(variant),
)
